import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Search, Building2 } from "lucide-react";

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email_general: "", phone: "", address: "", notes: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/suppliers", { params: { search, limit: 50 } });
      setSuppliers(res.data.suppliers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      await api.post("/suppliers", form);
      toast.success("Lieferant angelegt");
      setShowForm(false);
      setForm({ name: "", email_general: "", phone: "", address: "", notes: "" });
      load();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lieferanten</h1>
          <p className="text-sm text-gray-500 mt-0.5">{total} Lieferanten gesamt</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <Plus size={16} /> Neuer Lieferant
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Lieferanten suchen..."
          className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm" />
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4 shadow-sm">
          <h2 className="font-semibold text-gray-800 mb-4">Neuer Lieferant</h2>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="col-span-2 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="E-Mail" value={form.email_general} onChange={e => setForm({...form, email_general: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="Telefon" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <textarea placeholder="Adresse" value={form.address} onChange={e => setForm({...form, address: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" rows={2} />
            <textarea placeholder="Notizen" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" rows={2} />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSubmit} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Speichern</button>
            <button onClick={() => setShowForm(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        {loading ? (
          <p className="text-sm text-gray-400 p-6">Lade...</p>
        ) : suppliers.length === 0 ? (
          <p className="text-sm text-gray-400 p-6">Keine Lieferanten gefunden</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {suppliers.map(s => (
              <div key={s.id} onClick={() => navigate("/suppliers/" + s.id)}
                className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Building2 size={15} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.email_general || s.phone || "Keine Kontaktdaten"}</p>
                  </div>
                </div>
                <span className={"text-xs font-medium px-2.5 py-1 rounded-full " + (s.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400")}>
                  {s.is_active ? "Aktiv" : "Inaktiv"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
